/**
 * adsDrafts.ts — the shapes of an ad draft as stored, edited and reviewed, and the single function that checks one.
 * The Worker, the review screen and the tests all use these, so a draft is judged the same way everywhere.
 */
import { z } from "zod";
import { INTENT_CHECKS, adProblemSchema, adChannelSchema, adProductIdSchema, linkedInGroupSchema, type AdChannel, type AdProductId } from "./adsProducts.ts";
import {
  checkAssets,
  checkKeyword,
  checkLinkedInAd,
  checkNegative,
  checkRsa,
  hasFixes,
  type AdProblem,
  type CheckContext,
} from "./adsChecks.ts";
import { adImageKitSchema } from "./adsBrand.ts";
import { adFeedbackTagSchema, adVerdictSchema } from "./adsFeedback.ts";
import { testKindSchema } from "./adsInsights.ts";

const line = (max: number) => z.string().trim().max(max);

export const linkedInPayloadSchema = z.object({
  channel: z.literal("linkedin"),
  group: linkedInGroupSchema,
  postText: z.string().trim().max(3000),
  headline: line(300),
  imageHook: line(120),
  /** Up to two words of the hook that are printed in the accent colour. */
  highlight: z.array(line(40)).max(2).default([]),
  /** Which supporting visual the image uses (from the product's list). */
  visual: line(80).default("none"),
  /** An AI picture (from the picture studio) used as the image background. The words and logo are still drawn in code on top. */
  backgroundImageId: z.string().max(80).nullable().default(null),
  /** The AI scene (a picture with no words) drawn behind the hook. Made automatically or by "Regenerate scene"; null when none. */
  sceneImageId: z.string().max(80).nullable().default(null),
  /** v92: the scene made for the wide 1200×627 crop. Ads made before keep null and show the square scene cropped. */
  sceneLandscapeImageId: z.string().max(80).nullable().default(null),
});
export type LinkedInPayload = z.infer<typeof linkedInPayloadSchema>;

export const googlePayloadSchema = z.object({
  channel: z.literal("google_search"),
  adGroup: line(120),
  keyword: line(120).default(""),
  /** The page the ad sends people to (on the product's own site). Needed before the ad can be exported to Google Ads Editor. */
  finalUrl: line(500).default(""),
  headlines: z.array(line(200)).max(20),
  descriptions: z.array(line(300)).max(8),
  path1: line(40).default(""),
  path2: line(40).default(""),
  sitelinks: z.array(z.object({ text: line(60), description1: line(80).optional(), description2: line(80).optional() })).max(8).default([]),
  callouts: z.array(line(60)).max(12).default([]),
  snippets: z.array(line(80)).max(4).default([]),
  keywords: z
    .array(z.object({
      term: line(120),
      matchType: line(20),
      monthlyVolume: z.number().int().min(0).nullable(),
      fromOwnTermsOrCompetitorPays: z.boolean().default(false),
      evidence: line(500),
    }))
    .max(60)
    .default([]),
  negatives: z.array(z.object({ term: line(120), intentCheck: z.enum(INTENT_CHECKS).nullable() })).max(100).default([]),
});
export type GooglePayload = z.infer<typeof googlePayloadSchema>;

export const adPayloadSchema = z.discriminatedUnion("channel", [linkedInPayloadSchema, googlePayloadSchema]);
export type AdPayload = z.infer<typeof adPayloadSchema>;

export const AD_STATUSES = ["in_review", "approved", "needs_edit", "rejected", "superseded"] as const;
export const adStatusSchema = z.enum(AD_STATUSES);
export type AdStatus = z.infer<typeof adStatusSchema>;
export const AD_STATUS_LABEL: Record<AdStatus, string> = {
  in_review: "Waiting for review",
  approved: "Love it",
  needs_edit: "Use with edits",
  rejected: "Not for us",
  superseded: "Replaced by a newer draft",
};

/** Checks one draft. Returns every problem; `hasFixes(problems)` says whether it may be approved. */
export function checkCreative(payload: AdPayload, ctx: CheckContext): AdProblem[] {
  if (payload.channel === "linkedin") {
    return checkLinkedInAd(
      { group: payload.group, postText: payload.postText, headline: payload.headline, imageHook: payload.imageHook },
      ctx,
      payload.highlight
    );
  }
  const out: AdProblem[] = [];
  out.push(...checkRsa({ headlines: payload.headlines, descriptions: payload.descriptions, path1: payload.path1, path2: payload.path2, keyword: payload.keyword }, ctx));
  out.push(...checkAssets({ sitelinks: payload.sitelinks, callouts: payload.callouts, snippets: payload.snippets }, ctx));
  for (const k of payload.keywords) out.push(...checkKeyword(k, ctx.product));
  for (const n of payload.negatives) out.push(...checkNegative(n, ctx.product));
  return out;
}
export { hasFixes };

// --------------------------------------------------------------------------------------------------------------------
// API shapes
// --------------------------------------------------------------------------------------------------------------------

/** `flagged` is set only on a second-opinion reviewer's "flag" (which, like all reviewer notes, never blocks): it lets the screen tell a flag from a suggestion. */
export { adProblemSchema };

/** What an imported ad carried that has no column of its own. Every field may be missing. */
export const adLegacySchema = z.object({
  source: z.string().nullish(),
  adName: z.string().nullish(),
  teamMember: z.string().nullish(),
  imageBrief: z.string().nullish(),
  weekOf: z.string().nullish(),
  postUrl: z.string().nullish(),
  notes: z.string().nullish(),
  status: z.string().nullish(),
});
export type AdLegacy = z.infer<typeof adLegacySchema>;

/** How an idea is built, in the model's own words but from fixed lists, so ideas can be counted and compared. */
export const AD_HOOK_TACTICS = ["question", "number", "contrarian", "pain", "proof", "story"] as const;
export const AD_OFFER_TYPES = ["free_trial", "early_access", "demo", "none"] as const;
export const adConceptTagsSchema = z.object({
  hookTactic: z.enum(AD_HOOK_TACTICS),
  offerType: z.enum(AD_OFFER_TYPES),
  /** The call-to-action style in a few words, e.g. "Get Early Access". */
  ctaStyle: z.string().trim().max(80),
});
export type AdConceptTags = z.infer<typeof adConceptTagsSchema>;
/** Reads the stored tags column: the tags, or null when it is "{}", empty or not readable. */
export function parseConceptTags(raw: string | null | undefined): AdConceptTags | null {
  if (!raw) return null;
  try {
    const t = adConceptTagsSchema.safeParse(JSON.parse(raw));
    return t.success ? t.data : null;
  } catch {
    return null;
  }
}
export const AD_HOOK_TACTIC_LABEL: Record<(typeof AD_HOOK_TACTICS)[number], string> = { question: "Question", number: "Number", contrarian: "Contrarian", pain: "Pain point", proof: "Proof", story: "Story" };
export const AD_OFFER_TYPE_LABEL: Record<(typeof AD_OFFER_TYPES)[number], string> = { free_trial: "Free trial", early_access: "Early access", demo: "Demo", none: "No offer" };

export const adDraftSchema = z.object({
  id: z.string(),
  product: adProductIdSchema,
  channel: adChannelSchema,
  status: adStatusSchema,
  payload: adPayloadSchema,
  problems: z.array(adProblemSchema),
  concept: z
    .object({
      id: z.string(),
      angle: z.string(),
      buyer: z.string(),
      evidence: z.string(),
      confidence: z.number().nullable(),
      whyItWins: z.string().default(""),
      evidenceType: z.string().nullable().default(null),
      evidenceLink: z.string().nullable().default(null),
      /** 1 to 5, with the "Up: … Down: …" reasons. */
      score: z.number().int().nullable().default(null),
      scoreReason: z.string().default(""),
      graphicDescription: z.string().default(""),
      smallPractice: z.boolean().default(false),
      /** What this ad tests against an earlier one, or null. */
      testKind: testKindSchema.nullable().default(null),
      /** Which model or person suggested it ("Claude", "Grok", "Both"). */
      suggestedBy: z.string().nullable().default(null),
      tags: adConceptTagsSchema.nullable().default(null),
      writtenBy: z.string().nullable().default(null),
      /** Where the idea started ("competitor_ad:<id>", "brief", …), or null. */
      origin: z.string().nullable().default(null),
    })
    .nullable(),
  /** The model that wrote it, for AI-written ads. */
  model: z.string().nullable().default(null),
  regenNote: z.string().nullable(),
  parentId: z.string().nullable(),
  imageSquareKey: z.string().nullable(),
  imageLandscapeKey: z.string().nullable(),
  autoSelected: z.boolean(),
  addedByType: z.enum(["human", "ai", "airtable"]),
  addedBy: z.string().nullable(),
  addedByName: z.string().nullable(),
  createdAt: z.string(),
  reviewedBy: z.string().nullable(),
  reviewedByName: z.string().nullable(),
  reviewedAt: z.string().nullable(),
  retiredAt: z.string().nullable(),
  /** When this ad last went into a Google Ads Editor file (Google ads only). */
  exportedAt: z.string().nullable().default(null),
  /** Kept as an approved example of our tone and structure; the AI is shown it when writing new ads. */
  isReference: z.boolean().default(false),
  /** Details kept from an imported ad; null for ads written here. */
  legacy: adLegacySchema.nullable().default(null),
});
export type AdDraft = z.infer<typeof adDraftSchema>;

export const adDraftsQuerySchema = z.object({
  siteId: z.string().min(1).max(100),
  status: adStatusSchema.optional(),
  product: adProductIdSchema.optional(),
  channel: adChannelSchema.optional(),
  includeRetired: z.enum(["true", "false"]).default("false"),
  /** "true" lists only the ads kept as copy references. */
  reference: z.enum(["true", "false"]).optional(),
  /** Newest first. Default 100 so one request never reads the whole table. */
  limit: z.coerce.number().int().min(1).max(200).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});
export const adDraftsResponseSchema = z.object({ items: z.array(adDraftSchema) });

export const adConceptInputSchema = z.object({
  angle: z.string().trim().min(1).max(300),
  buyer: z.string().trim().max(300).default(""),
  evidence: z.string().trim().max(2000).default(""),
  confidence: z.number().min(0).max(1).nullable().default(null),
  whyItWins: z.string().trim().max(1000).default(""),
  evidenceType: z.string().max(100).nullable().default(null),
  evidenceLink: z.string().max(1000).nullable().default(null),
  score: z.number().int().min(1).max(5).nullable().default(null),
  scoreReason: z.string().trim().max(1000).default(""),
  graphicDescription: z.string().trim().max(1000).default(""),
  /** Raya only: aimed at small or solo practices (at most about a third of each week). */
  smallPractice: z.boolean().default(false),
  testKind: testKindSchema.nullable().default(null),
  suggestedBy: z.string().max(60).nullable().default(null),
  /** Migration 0066. Null for ideas written by a person or before the two-model run. */
  tags: adConceptTagsSchema.nullable().default(null),
  /** The model id that wrote the idea ("claude-…", "grok-…"), or null for a person. */
  writtenBy: z.string().max(100).nullable().default(null),
});
export const adDraftCreateSchema = z.object({
  siteId: z.string().min(1).max(100),
  product: adProductIdSchema,
  payload: adPayloadSchema,
  concept: adConceptInputSchema.optional(),
});
export const adDraftEditSchema = z.object({ siteId: z.string().min(1).max(100), payload: adPayloadSchema });
export const adDraftReviewSchema = z.object({
  siteId: z.string().min(1).max(100),
  verdict: adVerdictSchema,
  tags: z.array(adFeedbackTagSchema).max(11).default([]),
  note: z.string().trim().max(1000).optional(),
});
export const adReferenceBodySchema = z.object({ siteId: z.string().min(1).max(100), on: z.boolean() });
export const adDraftRetireSchema = z.object({ siteId: z.string().min(1).max(100), reason: z.string().trim().max(300).optional() });

export const adFeedbackRulesQuerySchema = z.object({ siteId: z.string().min(1).max(100), product: adProductIdSchema, channel: adChannelSchema });

export type { AdChannel, AdProductId };

/** Where a new concept starts: someone else's ad, one of our own ads, or a person's brief. */
export const AD_ORIGIN_KINDS = ["competitor_ad", "winning_creative", "tired_creative", "brief"] as const;
export const adOriginKindSchema = z.enum(AD_ORIGIN_KINDS);
export type AdOriginKind = z.infer<typeof adOriginKindSchema>;
export const AD_ORIGIN_LABEL: Record<AdOriginKind, string> = { competitor_ad: "A competitor ad", winning_creative: "An approved ad", tired_creative: "A tired ad", brief: "A brief" };

/** What the caller sends: a reference to the starting ad (the Worker reads it), or the text of a brief. */
export const adOriginSchema = z
  .object({ kind: adOriginKindSchema, ref: z.string().trim().min(1).max(100).optional(), text: z.string().trim().min(1).max(2000).optional() })
  .superRefine((o, ctx) => {
    if (o.kind === "brief" && !o.text) ctx.addIssue({ code: "custom", path: ["text"], message: "Write the brief." });
    if (o.kind !== "brief" && !o.ref) ctx.addIssue({ code: "custom", path: ["ref"], message: "Choose the ad to start from." });
  });
export type AdOrigin = z.infer<typeof adOriginSchema>;

/** What the AI Worker gets: the kind and the source text (read from the database, never from the browser). */
export const adOriginForModelSchema = z.object({ kind: adOriginKindSchema, text: z.string().trim().min(1).max(2500) });
export type AdOriginForModel = z.infer<typeof adOriginForModelSchema>;

/** The instruction that goes with each starting point. The source text itself is data. */
export const ORIGIN_GUIDANCE: Record<AdOriginKind, string> = {
  competitor_ad: "Reference only. Never reuse its hook wording or name the advertiser. Find the angle it misses.",
  winning_creative: "Iterate on this approved ad's angle with new evidence.",
  tired_creative: "Same angle, new hook and visual.",
  brief: "A person wrote this brief. Build the concepts from it, within the fact sheet and the rules.",
};

export const adGenerateBodySchema = z.object({
  siteId: z.string().min(1).max(100),
  product: adProductIdSchema,
  channel: adChannelSchema,
  count: z.number().int().min(1).max(5).default(3),
  /** Google only: the ad group and core keyword. */
  target: z.object({ adGroup: z.string().trim().min(1).max(120), keyword: z.string().trim().min(1).max(120) }).optional(),
  /** Start from a competitor ad, an approved or tired ad of ours, or a written brief. */
  origin: adOriginSchema.optional(),
});
/** POST /ad-drafts/:id/scene — make the AI scene again. `note` is added to the end of the picture prompt. */
export const adSceneBodySchema = z.object({ siteId: z.string().min(1), note: z.string().trim().max(300).default("") });
export const adSceneResultSchema = z.object({ sceneImageId: z.string(), sceneLandscapeImageId: z.string().nullable().default(null), costUsd: z.number() });
export type AdSceneResult = z.infer<typeof adSceneResultSchema>;
/** "Test style": one throwaway scene from the style and guardrails on screen (saved or not). */
export const adImageTestBodySchema = z.object({
  siteId: z.string().min(1),
  product: adProductIdSchema,
  image: adImageKitSchema,
  hook: z.string().trim().max(120).default(""),
});

export const adRegenerateBodySchema = z.object({ siteId: z.string().min(1).max(100), note: z.string().trim().min(1).max(1000) });
export const adGenerateResultSchema = z.object({ runId: z.string(), created: z.array(z.string()), dropped: z.number().int(), costUsd: z.number().nullable() });

// --------------------------------------------------------------------------------------------------------------------
// Google Ads Editor file
// --------------------------------------------------------------------------------------------------------------------

export const adEditorExportBodySchema = z.object({
  siteId: z.string().min(1).max(100),
  product: adProductIdSchema,
  /** Also include drafts that were exported before (for example after Editor flagged a problem and the copy was fixed). */
  again: z.boolean().default(false),
});
export type AdEditorExportBody = z.infer<typeof adEditorExportBodySchema>;

export const adEditorExportSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  campaign: z.string(),
  csv: z.string(),
  createdAt: z.string(),
  counts: z.object({ adGroups: z.number().int(), ads: z.number().int(), keywords: z.number().int(), negatives: z.number().int(), callouts: z.number().int() }),
  skipped: z.array(z.object({ id: z.string(), adGroup: z.string(), reason: z.string() })),
  byHand: z.array(z.string()),
  steps: z.array(z.string()),
});
export type AdEditorExport = z.infer<typeof adEditorExportSchema>;

export const adEditorExportListSchema = z.object({
  items: z.array(z.object({ id: z.string(), fileName: z.string(), product: adProductIdSchema, campaign: z.string(), ads: z.number().int(), createdAt: z.string(), createdByName: z.string().nullable() })),
});
